import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { LoginRequestSchema, type LoginRequest, HttpProblemError } from '@collector/shared'
import { useAuth } from '../lib/auth'

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [serverError, setServerError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({ resolver: zodResolver(LoginRequestSchema) })

  const onSubmit = async (values: LoginRequest) => {
    setServerError(null)
    try {
      await auth.login(values.username, values.password)
      const dest = (location.state as { from?: string } | null)?.from ?? '/'
      void navigate(dest, { replace: true })
    } catch (err) {
      if (err instanceof HttpProblemError) {
        setServerError(err.problem.detail ?? err.problem.title)
        return
      }
      setServerError(t('common.unexpectedError'))
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4 dark:bg-stone-950">
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e)
        }}
        className="w-full max-w-sm space-y-4 rounded-lg border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-900"
      >
        <div>
          <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t('login.title')}
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400">{t('login.subtitle')}</p>
        </div>
        <div>
          <label className="label">{t('login.username')}</label>
          <input
            className="input"
            type="text"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            {...register('username')}
          />
          {errors.username ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.username.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">{t('login.password')}</label>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            {...register('password')}
          />
          {errors.password ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.password.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{serverError}</p>
        ) : null}
        <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
          {isSubmitting ? t('login.signingIn') : t('login.signIn')}
        </button>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('login.forgot')}{' '}
          <Link to="/recover" className="underline">
            {t('login.resetLink')}
          </Link>
          .
        </p>
      </form>
    </div>
  )
}
